<template>
    <div class="pb-5 reservation-export">
        <LoaderComponent v-if="nbLoading > 0" />
        <div v-else>
            <div class="row m-0">
                <div class="col-12">
                    <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 pt-2 mt-1 mb-1">
                        <h5 class="title mt-2">{{ $tl("labels.booking.reservations.export.title", restaurantId) }}</h5>
                    </div>
                </div>
            </div>
            <form class="advance-filter reservation-export-form" @submit.prevent>
                <div class="export-grid">
                    <div class="export-filters">
                        <section class="export-card" aria-labelledby="export-period-title">
                            <h6 id="export-period-title" class="export-card-title">{{ $t("labels.booking.reservations.export.period") }}</h6>
                            <div class="export-period date-resa-cal">
                                <label class="export-period-label" for="export-from">{{ $tl("labels.filters.dates.from", restaurantId) }}</label>
                                <DatePicker
                                    id="export-from"
                                    input-class="form-control"
                                    v-model="from"
                                    @selected="
                                        setEndDateFromFromDate();
                                        fetchServices();
                                    " />
                                <label class="export-period-label" for="export-to">{{
                                    $tl("labels.filters.dates.to", restaurantId).toLowerCase()
                                }}</label>
                                <DatePicker
                                    id="export-to"
                                    input-class="form-control"
                                    :disabledDates="disabledDates"
                                    v-model="to"
                                    @selected="fetchServices" />
                            </div>
                        </section>
                        <section class="export-card" aria-labelledby="export-status-title">
                            <div class="export-card-header">
                                <h6 id="export-status-title" class="export-card-title">
                                    {{ $tl("labels.filters.statuses", restaurantId) }}
                                    <span class="export-count">{{ status.length }}/{{ ALL_RESERVATION_STATUS_FOR_FILTER.length }}</span>
                                </h6>
                                <button type="button" class="export-toggle-all" @click="toggleAllStatusChecked">
                                    {{ $tl(areAllStatusChecked ? "labels.uncheckAll" : "labels.checkAll") }}
                                </button>
                            </div>
                            <div class="export-chips" role="group" aria-labelledby="export-status-title">
                                <label v-for="s in ALL_RESERVATION_STATUS_FOR_FILTER" :key="s.value" class="export-chip">
                                    <input type="checkbox" class="export-chip-input" v-model="status" :value="s.value" />
                                    <span class="export-chip-body">
                                        <span class="export-chip-mark" aria-hidden="true"></span>
                                        {{ getReservationStatusLabel(s.value, undefined, restaurantId) }}
                                    </span>
                                </label>
                            </div>
                        </section>
                        <section class="export-card" aria-labelledby="export-services-title">
                            <div class="export-card-header">
                                <h6 id="export-services-title" class="export-card-title">
                                    {{ $tl("labels.services", restaurantId) }}
                                    <span class="export-count">{{ services.length }}/{{ servicesForFilter.length }}</span>
                                </h6>
                                <button v-if="servicesForFilter.length" type="button" class="export-toggle-all" @click="toggleAllServicesChecked">
                                    {{ $tl(areAllServicesChecked ? "labels.uncheckAll" : "labels.checkAll") }}
                                </button>
                            </div>
                            <div class="export-chips" role="group" aria-labelledby="export-services-title">
                                <label v-for="s in servicesForFilter" :key="s.id" class="export-chip">
                                    <input type="checkbox" class="export-chip-input" v-model="services" :value="s.id" />
                                    <span class="export-chip-body">
                                        <span class="export-chip-mark" aria-hidden="true"></span>
                                        {{ s.name }}
                                    </span>
                                </label>
                            </div>
                        </section>
                    </div>
                    <section class="export-card" aria-labelledby="export-columns-title">
                        <div class="export-card-header">
                            <h6 id="export-columns-title" class="export-card-title">
                                {{ $tl("labels.filters.columns", restaurantId) }}
                                <span class="export-count">{{ columns.length }}/{{ FILTERED_RESERVATION_COLUMNS_FOR_EXPORT.length }}</span>
                            </h6>
                            <button type="button" class="export-toggle-all" @click="toggleAllColumnsChecked">
                                {{ $tl(areAllColumnsChecked ? "labels.uncheckAll" : "labels.checkAll") }}
                            </button>
                        </div>
                        <div class="export-pdf-limit" :class="{ 'is-over': exceedsPdfColumnLimit }">
                            <div class="export-pdf-meter" aria-hidden="true">
                                <span
                                    v-for="n in pdfColumnLimit"
                                    :key="n"
                                    class="export-pdf-meter-step"
                                    :class="{ 'is-filled': n <= columns.length }"></span>
                            </div>
                            <p id="pdf-column-guidance" class="export-pdf-text" role="status" aria-live="polite">
                                <strong>{{
                                    $t("labels.booking.reservations.export.pdfSelection", { count: columns.length, limit: pdfColumnLimit })
                                }}</strong>
                                {{ $t("labels.booking.reservations.export.pdfLimitHint") }}
                            </p>
                            <button type="button" class="btn btn-sm btn-circle export-preset" @click="selectPdfColumns">
                                {{ $t("labels.booking.reservations.export.pdfPreset") }}
                            </button>
                        </div>
                        <div class="export-chips" role="group" aria-labelledby="export-columns-title">
                            <label v-for="c in FILTERED_RESERVATION_COLUMNS_FOR_EXPORT" :key="c.value" class="export-chip">
                                <input type="checkbox" class="export-chip-input" v-model="columns" :value="c.value" />
                                <span class="export-chip-body">
                                    <span class="export-chip-mark" aria-hidden="true"></span>
                                    {{ getReservationColumnForExportLabel(c.value, restaurantId) }}
                                </span>
                            </label>
                        </div>
                    </section>
                </div>
                <div class="export-actions">
                    <p v-if="pdfHint" id="export-pdf-hint" class="export-actions-hint">
                        <feather type="info" aria-hidden="true" />
                        <span>{{ pdfHint }}</span>
                    </p>
                    <div class="export-actions-buttons">
                        <button
                            type="button"
                            @click.prevent="exportReservationsExcel"
                            class="btn btn-sm btn-outline-success btn-circle export-action">
                            <feather type="download-cloud" />
                            {{ $tl("labels.form.actions.export", restaurantId) }} (Excel)
                        </button>
                        <button
                            type="button"
                            @click.prevent="printReservations"
                            :disabled="!canPrintPdf"
                            :aria-describedby="pdfHint ? 'export-pdf-hint pdf-column-guidance' : 'pdf-column-guidance'"
                            class="btn btn-sm btn-success btn-circle export-action">
                            <feather type="printer" />
                            {{ $tl("labels.form.actions.print", restaurantId) }} (PDF)
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import LoaderComponent from "../../components/LoaderComponent.vue";
import DatePicker from "../../components/forms/DatePicker.vue";
import ReservationStatusEnum from "../../mixins/enums/booking/ReservationStatusEnum.js";
import ReservationColumnsForExportEnum from "../../mixins/enums/booking/ReservationColumnsForExportEnum.js";

export default {
    name: "ExportReservations",
    data() {
        return {
            nbLoading: 0,
            from: this.$route.query.date ? this.getDateTime(this.$route.query.date, false) : this.getDateTime(),
            to: this.$route.query.date ? this.getDateTime(this.$route.query.date, false) : this.getDateTime(),
            status: [],
            services: [],
            servicesForFilter: [],
            servicesFromSavedFilters: [],
            columns: [],
            pdfColumnLimit: 10,
        };
    },
    mixins: [ReservationStatusEnum, ReservationColumnsForExportEnum],
    computed: {
        exceedsPdfColumnLimit() {
            return this.columns.length > this.pdfColumnLimit;
        },
        canPrintPdf() {
            return this.columns.length > 0 && !this.exceedsPdfColumnLimit;
        },
        pdfHint() {
            if (this.status.length === 0) {
                return this.$tl("errors.booking.reservations.export.atLeastOneStatus", this.restaurantId);
            }
            if (this.services.length === 0) {
                return this.$tl("errors.booking.reservations.export.atLeastOneService", this.restaurantId);
            }
            if (this.columns.length === 0) {
                return this.$tl("errors.booking.reservations.export.atLeastOneColumn", this.restaurantId);
            }
            if (this.exceedsPdfColumnLimit) {
                return this.$t("labels.booking.reservations.export.pdfOverLimit", { count: this.columns.length - this.pdfColumnLimit });
            }
            return null;
        },
        areAllStatusChecked() {
            return this.ALL_RESERVATION_STATUS_FOR_FILTER.length === this.status.length;
        },
        areAllServicesChecked() {
            return this.servicesForFilter.length === this.services.length;
        },
        areAllColumnsChecked() {
            return this.FILTERED_RESERVATION_COLUMNS_FOR_EXPORT.length === this.columns.length;
        },
        restaurantId() {
            return Number.parseInt(this.$route.params.restaurant_id);
        },
        disabledDates() {
            if (this.from) {
                return {
                    to: this.from.minus({ days: 1 }).toJSDateCustom(),
                };
            }
            return {};
        },
        ALL_RESERVATION_STATUS_FOR_FILTER() {
            return this.ALL_RESERVATION_STATUS.filter((s) => s.value !== this.RESERVATION_STATUS_PENDING.value);
        },
        FILTERED_RESERVATION_COLUMNS_FOR_EXPORT() {
            return this.getFilteredReservationColumnsForExport({ addRoomNumber: this.shouldAddRoomNumber });
        },
        shouldAddRoomNumber() {
            return this.isFeatRoomNumbersEnable || this.isHotelModeEnabled;
        },
        isFeatRoomNumbersEnable() {
            const restaurant = this.$store.getters["restaurants/findRestaurantById"](this.restaurantId);

            return restaurant ? restaurant.feat_room_numbers : false;
        },
        isHotelModeEnabled() {
            return this.$store.getters["widgets/getWidget"].hotel_mode;
        },
    },
    methods: {
        selectPdfColumns() {
            const preset = ["reservation_date", "slot", "pax", "status", "table", "lastname", "firstname", "public_comment"];
            this.columns = this.FILTERED_RESERVATION_COLUMNS_FOR_EXPORT.filter((column) => preset.includes(column.value)).map(
                (column) => column.value
            );
        },
        toggleAllStatusChecked() {
            if (this.areAllStatusChecked) {
                this.status = [];
            } else {
                this.status = this.arrayPluck("value", this.ALL_RESERVATION_STATUS_FOR_FILTER);
            }
        },
        toggleAllServicesChecked() {
            if (this.areAllServicesChecked) {
                this.services = [];
            } else {
                this.services = this.arrayPluck("id", this.servicesForFilter);
            }
        },
        toggleAllColumnsChecked() {
            if (this.areAllColumnsChecked) {
                this.columns = [];
            } else {
                this.columns = this.arrayPluck("value", this.FILTERED_RESERVATION_COLUMNS_FOR_EXPORT);
            }
        },
        setEndDateFromFromDate() {
            this.$nextTick(() => {
                if (this.from > this.to) {
                    this.to = this.from;
                }
            });
        },
        validateFilters(col_limit = undefined) {
            let errorMsgs = [];

            if (this.from && this.to && this.from > this.to) {
                errorMsgs.push(this.$tl("errors.common.dates.endGreaterThanStart", this.restaurantId));
            }

            if (this.status.length === 0) {
                errorMsgs.push(this.$tl("errors.booking.reservations.export.atLeastOneStatus", this.restaurantId));
            }

            if (this.services.length === 0) {
                errorMsgs.push(this.$tl("errors.booking.reservations.export.atLeastOneService", this.restaurantId));
            }

            if (this.columns.length === 0) {
                errorMsgs.push(this.$tl("errors.booking.reservations.export.atLeastOneColumn", this.restaurantId));
            } else if (typeof col_limit != "undefined" && this.columns.length > col_limit) {
                errorMsgs.push(
                    this.$t("errors.booking.reservations.export.maxColumns", {
                        limit: col_limit,
                        nb: this.columns.length,
                    })
                );
            }

            if (errorMsgs.length > 0) {
                this.notifyError(null, errorMsgs.join("\n"));

                return false;
            }

            return true;
        },
        getURLParams() {
            return (
                `&from=${this.from.toISODate()}&to=${this.to.toISODate()}&status=${this.status.join("-")}` +
                `&services=${this.services.join("-")}&columns=${this.columns.join("-")}`
            );
        },
        exportReservationsExcel() {
            if (!this.validateFilters()) {
                return;
            }

            let urlParams = this.getURLParams();

            if (confirm(this.$tl("questions.exports.saveFilters", this.restaurantId))) {
                urlParams += "&persist_filters=1";
            }

            window.open(this.addLangToURL(`/api/restaurants/${this.restaurantId}/reservations/export?${urlParams}`), "_blank");
        },
        printReservations() {
            if (!this.validateFilters(this.pdfColumnLimit)) {
                return;
            }

            let urlParams = this.getURLParams();

            if (confirm(this.$tl("questions.exports.saveFilters", this.restaurantId))) {
                urlParams += "&persist_filters=1";
            }

            window.open(this.addLangToURL(`/api/restaurants/${this.restaurantId}/reservations/print?${urlParams}`), "_blank");
        },
        initData() {
            this.nbLoading++;

            this.fetchFiltersFromApi().finally(() => {
                this.fetchServices().finally(() => this.nbLoading--);
            });
        },
        fetchServices() {
            this.nbLoading++;

            return new Promise((resolve) => {
                this.$nextTick(() => {
                    this.httpGet(`/api/restaurants/${this.restaurantId}/services/openOnDates?from=${this.from.toISODate()}&to=${this.to.toISODate()}`)
                        .then((response) => {
                            if (response !== false) {
                                this.servicesForFilter = response.data.data;

                                if (this.servicesFromSavedFilters.length > 0) {
                                    this.services = this.arrayPluck(
                                        "id",
                                        this.servicesForFilter.filter((service) => this.servicesFromSavedFilters.includes(service.id))
                                    );
                                } else {
                                    this.services = this.arrayPluck("id", this.servicesForFilter);
                                }
                            }
                        })
                        .finally(() => {
                            this.nbLoading--;
                            resolve();
                        });
                });
            });
        },
        fetchFiltersFromApi() {
            this.nbLoading++;

            return this.httpGet(`/api/exports/filters?export_class=App\\Exports\\ReservationExport`, { handleReject: false })
                .then((response) => {
                    if (response !== false) {
                        if (response.data.export_filters) {
                            this.syncFiltersFromApi(response.data.export_filters);
                        }
                    }
                })
                .finally(() => this.nbLoading--);
        },
        syncFiltersFromApi(filters) {
            if (filters.status) {
                this.status = filters.status;
            }

            if (filters.services) {
                this.services = filters.services;
                this.servicesFromSavedFilters = filters.services;
            }

            if (filters.columns) {
                this.columns = filters.columns;
            }
        },
        initColumns() {
            this.columns = this.arrayPluck("value", this.FILTERED_RESERVATION_COLUMNS_FOR_EXPORT);
        },
    },
    watch: {
        shouldAddRoomNumber() {
            this.initColumns();
        },
    },
    components: {
        LoaderComponent,
        DatePicker,
    },
    created() {
        this.initColumns();

        this.status = this.arrayPluck("value", this.ALL_RESERVATION_STATUS_FOR_FILTER);

        this.initData();
    },
};
</script>

<style scoped>
.reservation-export {
    --export-accent: var(--main-color-light, #30a4b7);
    --export-accent-soft: #eaf6f8;
    --export-accent-border: #b9e0e6;
    --export-ink: #2f3b45;
    --export-muted: #6b7680;
    --export-line: #e3e8ec;
    --export-danger: #c0392b;
    --export-danger-soft: #fdf0ef;
    color: var(--export-ink);
}

@supports (color: color-mix(in srgb, red 50%, white)) {
    .reservation-export {
        --export-accent-soft: color-mix(in srgb, var(--export-accent) 10%, white);
        --export-accent-border: color-mix(in srgb, var(--export-accent) 40%, white);
    }
}

.reservation-export-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0 0.75rem;
}

.export-card {
    background: #fff;
    border: 1px solid var(--export-line);
    border-radius: 14px;
    padding: 1.1rem 1.25rem 1.25rem;
}

.export-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.85rem;
}

.export-card-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--export-ink);
}

.export-card > .export-card-title {
    margin-bottom: 0.85rem;
}

.export-count {
    padding: 0.1rem 0.5rem;
    border-radius: 999px;
    background: #f1f4f6;
    color: var(--export-muted);
    font-size: 0.75rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.export-toggle-all {
    flex-shrink: 0;
    padding: 0.35rem 0.25rem;
    border: 0;
    background: none;
    color: var(--export-ink);
    font-size: 0.8rem;
    text-decoration: underline;
    text-underline-offset: 3px;
}

.export-toggle-all:hover {
    color: #000;
}

.export-period {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
}

.export-period-label {
    margin: 0;
    color: var(--export-muted);
}

.reservation-export :deep(.vdp-datepicker .form-control) {
    width: 12.5rem;
    min-height: 40px;
    border: 1px solid var(--export-line);
    border-radius: 10px;
    padding: 0.4rem 0.75rem;
    color: var(--export-ink);
    background: #fff;
    cursor: pointer;
}

.export-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    align-items: start;
}

.export-filters {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.export-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.export-chip {
    position: relative;
    margin: 0;
    cursor: pointer;
}

.export-chip-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;
}

.export-chip-body {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    min-height: 36px;
    padding: 0.35rem 0.8rem 0.35rem 0.6rem;
    border: 1px solid var(--export-line);
    border-radius: 999px;
    background: #fff;
    color: var(--export-muted);
    font-size: 0.85rem;
    line-height: 1.2;
    transition:
        background-color 0.15s ease,
        border-color 0.15s ease,
        color 0.15s ease;
}

.export-chip-mark {
    position: relative;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border: 1.5px solid #b8c1c8;
    border-radius: 5px;
    background: #fff;
}

.export-chip:hover .export-chip-body {
    border-color: var(--export-accent-border);
}

.export-chip-input:checked + .export-chip-body {
    border-color: var(--export-accent-border);
    background: var(--export-accent-soft);
    color: var(--export-ink);
}

.export-chip-input:checked + .export-chip-body .export-chip-mark {
    border-color: var(--export-accent);
    background: var(--export-accent);
}

.export-chip-input:checked + .export-chip-body .export-chip-mark::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 1px;
    width: 5px;
    height: 9px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.export-chip-input:focus-visible + .export-chip-body {
    outline: 2px solid var(--export-ink);
    outline-offset: 2px;
}

.export-pdf-limit {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
        "meter meter"
        "text preset";
    gap: 0.6rem 1rem;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.85rem 1rem;
    border: 1px solid var(--export-line);
    border-radius: 12px;
    background: #f8fafb;
}

.export-pdf-meter {
    grid-area: meter;
    display: flex;
    gap: 4px;
}

.export-pdf-meter-step {
    flex: 1;
    height: 6px;
    border-radius: 3px;
    background: #dfe5e9;
}

.export-pdf-meter-step.is-filled {
    background: var(--export-accent);
}

.export-pdf-text {
    grid-area: text;
    margin: 0;
    color: var(--export-muted);
    font-size: 0.8rem;
    line-height: 1.45;
}

.export-pdf-text strong {
    display: block;
    color: var(--export-ink);
    font-size: 0.85rem;
}

.export-preset {
    grid-area: preset;
    border: 1px solid #c9d2d8;
    background: #fff;
    color: var(--export-ink);
    font-weight: 500;
    white-space: nowrap;
}

.export-preset:hover,
.export-preset:focus-visible {
    border-color: var(--export-accent);
    background: var(--export-accent-soft);
    color: var(--export-ink);
}

.export-pdf-limit.is-over {
    border-color: #f2c4bf;
    background: var(--export-danger-soft);
}

.export-pdf-limit.is-over .export-pdf-meter-step {
    background: var(--export-danger);
}

.export-pdf-limit.is-over .export-pdf-text strong {
    color: var(--export-danger);
}

.export-actions {
    position: sticky;
    bottom: 0;
    z-index: 5;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.6rem 1rem;
    margin: 0 -0.75rem;
    padding: 0.75rem;
    border-top: 1px solid var(--export-line);
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 -6px 16px rgba(20, 40, 60, 0.06);
}

.export-actions-hint {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0 auto 0 0;
    color: var(--export-danger);
    font-size: 0.85rem;
    font-weight: 500;
}

.export-actions-hint :deep(svg) {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
}

.export-actions-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.export-action {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    min-height: 38px;
    padding: 0.4rem 1rem;
}

.export-action :deep(svg) {
    width: 16px;
    height: 16px;
}

.export-action:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

@media (min-width: 1100px) {
    .export-grid {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr);
    }
}

@media (max-width: 767px) {
    .reservation-export-form {
        padding: 0 0.5rem;
    }

    .export-card {
        padding: 1rem;
    }

    .export-period {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
    }

    .reservation-export :deep(.vdp-datepicker),
    .reservation-export :deep(.vdp-datepicker .form-control) {
        width: 100%;
    }

    .export-pdf-limit {
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas:
            "meter"
            "text"
            "preset";
    }

    .export-preset {
        justify-self: start;
        white-space: normal;
    }

    .export-actions {
        bottom: 50px;
        margin: 0 -0.5rem;
        padding: 0.65rem 0.5rem;
    }

    .export-actions-hint {
        flex-basis: 100%;
    }

    .export-actions-buttons {
        width: 100%;
    }

    .export-action {
        flex: 1 1 0;
        justify-content: center;
    }
}
</style>
